import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CoachChatState {
  unreadCount: number;
  refreshChat: number; // Counter to trigger refreshes
  currentStatus: string | null;
  /**
   * Track which message buttons (suggested actions) have been clicked.
   * Persisted to prevent buttons from becoming re-clickable after closing/reopening the chat.
   */
  clickedMessageIds: string[];
  /**
   * Whether the onboarding nudge has already auto-opened the chat once.
   * Persisted so the chat doesn't keep popping open on every page reload
   * while onboarding remains incomplete.
   */
  onboardingChatPromptShown: boolean;
  /** Whether persisted state has finished rehydrating from localStorage. */
  hasHydrated: boolean;
  setUnreadCount: (count: number) => void;
  decrementUnreadCount: () => void;
  triggerRefresh: () => void;
  setCurrentStatus: (status: string | null) => void;
  clearCurrentStatus: () => void;
  markMessageClicked: (id: string) => void;
  setOnboardingChatPromptShown: (shown: boolean) => void;
  setHasHydrated: (hydrated: boolean) => void;
}

export const useCoachChatStore = create<CoachChatState>()(
  persist(
    (set) => ({
      unreadCount: 0,
      refreshChat: 0,
      currentStatus: null,
      clickedMessageIds: [],
      onboardingChatPromptShown: false,
      hasHydrated: false,
      setUnreadCount: (count) => set({ unreadCount: count }),
      decrementUnreadCount: () =>
        set((state) => ({ unreadCount: Math.max(0, state.unreadCount - 1) })),
      triggerRefresh: () =>
        set((state) => ({ refreshChat: state.refreshChat + 1 })),
      setCurrentStatus: (status) => set({ currentStatus: status }),
      clearCurrentStatus: () => set({ currentStatus: null }),
      markMessageClicked: (id) =>
        set((state) => ({
          clickedMessageIds: state.clickedMessageIds.includes(id)
            ? state.clickedMessageIds
            : [...state.clickedMessageIds, id],
        })),
      setOnboardingChatPromptShown: (shown) =>
        set({ onboardingChatPromptShown: shown }),
      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: 'coach-chat-store',
      // Only persist clickedMessageIds/onboardingChatPromptShown to avoid side effects on counters/status
      partialize: (state) => ({
        clickedMessageIds: state.clickedMessageIds,
        onboardingChatPromptShown: state.onboardingChatPromptShown,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
